import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Remove from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

export interface MetricsCellProps {
  value: number | null | undefined;
  delta: number | null | undefined;
  format: 'currency' | 'number' | 'cpm';
}

const valueFormatter: Record<MetricsCellProps['format'], (value: number) => string> = {
  currency: value =>
    new Intl.NumberFormat('en-US', {
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: 'currency',
    }).format(value),
  number: value => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value),
  cpm: value =>
    new Intl.NumberFormat('en-US', {
      currency: 'USD',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      style: 'currency',
    }).format(value),
};

const roundDeltaToTenth = (delta: number | string | null | undefined): number | undefined => {
  const numericDelta = Number(delta);
  if (!Number.isFinite(numericDelta)) return undefined;
  const rounded = Number(numericDelta.toFixed(1));
  // Normalize negative zero so it is treated as zero
  return Object.is(rounded, -0) ? 0 : rounded;
};

const formatDelta = (delta: number | string | null | undefined): string | undefined => {
  const numericDelta = Number(delta);
  if (!Number.isFinite(numericDelta)) return undefined;
  const sign = numericDelta > 0 ? '+' : '';
  return `${sign}${numericDelta.toFixed(1)}%`;
};

export const MetricsCell = ({ value, delta, format }: MetricsCellProps) => {
  if (value === null || value === undefined) {
    return <Typography sx={{ color: 'text.disabled' }}>–</Typography>;
  }

  const formattedValue = valueFormatter[format](value);
  const roundedDelta = roundDeltaToTenth(delta);

  const showPositive = roundedDelta !== undefined && roundedDelta > 0;
  const showNegative = roundedDelta !== undefined && roundedDelta < 0;
  const showNeutral = roundedDelta === undefined || roundedDelta === 0;
  const tooltipText = roundedDelta !== undefined ? formatDelta(roundedDelta) : undefined;

  const icon = showPositive ? (
    <ArrowUpward sx={{ color: 'success.main', fontSize: 14 }} />
  ) : showNegative ? (
    <ArrowDownward sx={{ color: 'error.main', fontSize: 14 }} />
  ) : (
    <Remove sx={{ color: 'text.disabled', fontSize: 14 }} />
  );

  const deltaContent = showNeutral ? (
    icon
  ) : (
    <Tooltip title={tooltipText ?? ''} placement="top">
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
        {icon}
      </Box>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {formattedValue}
      </Typography>
      {deltaContent}
    </Box>
  );
};
